import React, { useState } from 'react';
import { useServices } from '../contexts/ServiceContext';
import ServiceCard from '../components/ServiceCard';
import AddServiceModal from '../components/AddServiceModal';
import EditServiceModal from '../components/EditServiceModal';

const Services = () => {
  const { services, deleteService } = useServices();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const handleEdit = (service) => {
    setEditingService(service);
  };

  const handleDelete = (service) => {
    if (window.confirm(`Are you sure you want to delete "${service.title}"?`)) {
      deleteService(service.id);
    }
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setEditingService(null);
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F5EFE6'}}>
      {/* Hero Section */}
      <section className="h-[400px] sm:h-[500px] md:h-[600px] relative flex items-center justify-center overflow-hidden">
        <div className="relative z-10 text-center px-4 sm:px-8">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 tracking-tighter" style={{color: '#3E3E3E'}}>
            Our Services
          </h1>
          <p className="text-base sm:text-lg md:text-xl max-w-xs sm:max-w-md md:max-w-2xl mx-auto" style={{color: '#7A7A7A'}}>
            Comprehensive design solutions tailored to your unique vision
          </p>
        </div>
      </section>

      {/* Add Service Button */}
      <div className="px-4 sm:px-8 md:px-16">
        <div className="max-w-7xl mx-auto flex justify-end mb-6">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all duration-300 text-sm sm:text-base"
            style={{backgroundColor: '#C8A96A', color: '#FFFFFF'}}
          >
            <span className="material-symbols-outlined">add</span>
            Add Service
          </button>
        </div>
      </div>

      {/* Services Grid */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-8 md:px-16">
        <div className="max-w-7xl mx-auto">
          {services.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 md:gap-16">
              {services.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <span className="material-symbols-outlined text-6xl text-stone-300 mb-4">design_services</span>
              <h3 className="text-xl font-semibold mb-2" style={{color: '#3E3E3E'}}>No services found</h3>
              <p style={{color: '#7A7A7A'}}>Start by adding your first service.</p>
            </div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-8 md:px-16" style={{backgroundColor: '#EDE3D3'}}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6" style={{color: '#3E3E3E'}}>Our Process</h2>
            <p className="text-base sm:text-lg md:text-xl max-w-2xl sm:max-w-3xl mx-auto" style={{color: '#7A7A7A'}}>
              A systematic approach to bringing your vision to life
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {[
              { step: "01", title: "Discovery", description: "Understanding your needs and aspirations" },
              { step: "02", title: "Concept", description: "Developing initial design concepts" },
              { step: "03", title: "Design", description: "Creating detailed plans and visualizations" },
              { step: "04", title: "Execution", description: "Bringing the design to reality" }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4" style={{color: '#C8A96A'}}>{process.step}</div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3" style={{color: '#3E3E3E'}}>{process.title}</h3>
                <p className="text-sm sm:text-base" style={{color: '#7A7A7A'}}>{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-8 md:px-16">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6" style={{color: '#3E3E3E'}}>Ready to Transform Your Space?</h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8" style={{color: '#7A7A7A'}}>
            Let's discuss how we can bring your interior design vision to life
          </p>
          <button className="px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-full font-bold hover:scale-105 transition-all duration-400 text-sm sm:text-base md:text-lg" style={{backgroundColor: '#C8A96A', color: '#FFFFFF'}}>
            Schedule Consultation
          </button>
        </div>
      </section>

      {/* Add Service Modal */}
      <AddServiceModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        onAddService={useServices().addService}
      />

      {/* Edit Service Modal */}
      <EditServiceModal
        isOpen={!!editingService}
        onClose={handleCloseEditModal}
        onEditService={useServices().updateService}
        service={editingService}
      />
    </div>
  );
};

export default Services;
